/**
 * NIRMAN-UI-002: Domain-Led Navigation Configuration
 *
 * Canonical Nirman domain definitions mapping to existing routes.
 * Each domain represents a top-level user goal or context.
 *
 * Architecture: Domain definitions are role-agnostic and route-agnostic.
 * Role visibility and route mapping are applied by consuming functions.
 */

import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import CodeOutlinedIcon from '@mui/icons-material/CodeOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import WorkOutlineOutlinedIcon from '@mui/icons-material/WorkOutlineOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';

/**
 * Domain icon map — Nirman domains to icon components.
 */
export const DOMAIN_ICON_MAP = {
  dashboard: DashboardOutlinedIcon,
  school: SchoolOutlinedIcon,
  code: CodeOutlinedIcon,
  assignment: AssignmentOutlinedIcon,
  trending: TrendingUpOutlinedIcon,
  work: WorkOutlineOutlinedIcon,
  group: GroupOutlinedIcon,
  notifications: NotificationsOutlinedIcon,
  settings: SettingsOutlinedIcon,
};

/**
 * NIRMAN Domains — canonical 9-domain navigation structure.
 *
 * Each domain is role-independent. Role visibility is applied via
 * getRoleVisibleDomains() and consumed by roleNavConfig functions.
 */
export const NIRMAN_DOMAINS = {
  home: {
    id: 'home',
    label: 'Home',
    description: 'Dashboard and overview',
    icon: 'dashboard',
    order: 1,
    collapsible: false,
  },
  learn: {
    id: 'learn',
    label: 'Learn',
    description: 'Learning resources and curriculum',
    icon: 'school',
    order: 2,
    collapsible: true,
  },
  practice: {
    id: 'practice',
    label: 'Practice',
    description: 'Coding problems and assignments',
    icon: 'code',
    order: 3,
    collapsible: true,
  },
  assessments: {
    id: 'assessments',
    label: 'Assessments',
    description: 'Exams and evaluations',
    icon: 'assignment',
    order: 4,
    collapsible: true,
  },
  progress: {
    id: 'progress',
    label: 'Progress',
    description: 'Learning analytics and insights',
    icon: 'trending',
    order: 5,
    collapsible: false,
  },
  placement: {
    id: 'placement',
    label: 'Placement',
    description: 'Career readiness and opportunities',
    icon: 'work',
    order: 6,
    collapsible: true,
  },
  community: {
    id: 'community',
    label: 'Community',
    description: 'Collaboration and discussion',
    icon: 'group',
    order: 7,
    collapsible: true,
  },
  notifications: {
    id: 'notifications',
    label: 'Notifications',
    description: 'Messages and alerts',
    icon: 'notifications',
    order: 8,
    collapsible: false,
  },
  settings: {
    id: 'settings',
    label: 'Settings',
    description: 'Preferences and account',
    icon: 'settings',
    order: 9,
    collapsible: false,
  },
};

/**
 * Domain to route mapping — which existing routes belong to each domain.
 * Used by getActiveDomain() to resolve current URL to active domain.
 *
 * Maps routes conservatively: only routes with clear domain semantics.
 * Routes matching multiple domains are assigned to most specific.
 */
export const DOMAIN_ROUTE_MAP = {
  home: [
    '/student/home',
    '/faculty/dashboard',
    '/admin/dashboard',
    '/hod/dashboard',
    '/placement/dashboard',
  ],
  learn: [
    '/student/lessons',
    '/faculty/lesson-plans',
    '/faculty/curriculum',
    '/admin/curriculum',
  ],
  practice: [
    '/student/practice',
    '/student/coding',
    '/student/assignments',
    '/student/labs',
    '/faculty/workspace',
    '/faculty/batch',
    '/faculty/coding-dashboard',
    '/admin/coding-tracker',
    '/admin/batch',
  ],
  assessments: [
    '/student/assessments',
    '/faculty/assessments',
    '/admin/assessments',
  ],
  progress: [
    '/student/progress',
    '/faculty/analytics',
    '/admin/analytics',
  ],
  placement: [
    '/student/placement',
    '/placement/coordinator',
    '/admin/placement',
  ],
  community: [
    '/student/community',
    '/faculty/community',
    '/admin/community',
  ],
  notifications: [
    '/notifications',
    '/inbox',
  ],
  settings: [
    '/student/settings',
    '/faculty/settings',
    '/admin/settings',
    '/hod/settings',
    '/profile',
  ],
};

/**
 * Role to domain visibility mapping.
 * Defines which domains are visible to each role.
 *
 * Roles: student, faculty, admin, hod, placement
 */
export const ROLE_DOMAIN_VISIBILITY = {
  student: [
    'home',
    'learn',
    'practice',
    'assessments',
    'progress',
    'community',
    'notifications',
    'settings',
  ],
  faculty: [
    'home',
    'learn',
    'practice',
    'assessments',
    'progress',
    'community',
    'notifications',
    'settings',
  ],
  admin: [
    'home',
    'learn',
    'practice',
    'assessments',
    'progress',
    'placement',
    'community',
    'notifications',
    'settings',
  ],
  hod: [
    'home',
    'learn',
    'practice',
    'assessments',
    'progress',
    'community',
    'notifications',
    'settings',
  ],
  placement: [
    'home',
    'placement',
    'notifications',
    'settings',
  ],
};

export default NIRMAN_DOMAINS;
